<template>
    <header class="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <!-- Main Navigation -->
        <nav class="container mx-auto px-4 lg:px-6">
            <div class="flex items-center justify-between h-16 lg:h-20">

                <!-- Left Section: Logo -->
                <div class="flex items-center">
                    <!-- Logo -->
                    <router-link to="/" class="flex items-center space-x-2 group">
                        <img src="@brand/assets/logo_underline.png" :alt="$brand.siteName"
                            class="h-10 lg:h-12 transition-transform duration-300 group-hover:scale-105">
                    </router-link>
                </div>

                <!-- Desktop Navigation -->
                <nav class="hidden lg:flex items-center space-x-1">
                    <!-- Products Mega Menu -->
                    <div class="relative" @mouseenter="showProductsMenu" @mouseleave="hideProductsMenu">
                        <router-link to="/products"
                            class="relative px-4 py-2 text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 group flex items-center space-x-1"
                            :class="{ 'text-red-600': $route.path === '/products' }">
                            <span>Produkty</span>
                        </router-link>
                    </div>

                    <!-- Categories Mega Menu -->
                    <div class="relative" @mouseenter="showCategoriesMenu" @mouseleave="hideCategoriesMenu">
                        <router-link to="/categories"
                            class="relative px-4 py-2 text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 group flex items-center space-x-1"
                            :class="{ 'text-red-600': $route.path === '/categories' }">
                            <span>Kategórie</span>
                        </router-link>
                    </div>

                    <router-link :to="$pagePath('about')"
                        class="relative px-4 py-2 text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 group"
                        :class="{ 'text-red-600': $route.path === $pagePath('about') }">
                        O nás
                    </router-link>

                    <!-- Contact Link -->
                    <router-link :to="$pagePath('contact')"
                        class="relative px-4 py-2 text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 group"
                        :class="{ 'text-red-600': $route.path === $pagePath('contact') }">
                        Kontakt
                    </router-link>
                </nav>

                <!-- Center Section: Search Bar -->
                <div class="hidden md:flex flex-1 max-w-2xl mx-8">
                    <div class="relative w-full" ref="searchDropdownRef">
                        <div class="relative">
                            <input v-model="searchQuery" type="text" placeholder="Hľadať produkty, kategórie..."
                                class="w-full pl-12 pr-12 py-3 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all duration-200 font-poppins"
                                @input="handleSearchInputEvent" @keydown="handleSearchKeydown" @focus="handleSearchFocus">
                            <Search class="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />

                            <!-- Clear button -->
                            <button v-if="searchQuery" @click="clearSearch"
                                class="absolute right-12 top-1/2 transform -translate-y-1/2 p-1 hover:text-red-600 transition-colors">
                                <X class="h-4 w-4 text-gray-400" />
                            </button>

                            <!-- Search button -->
                            <button @click="performSearch"
                                class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors">
                                <Search class="h-4 w-4" />
                            </button>
                        </div>

                        <!-- Search Dropdown -->
                        <div v-if="isDropdownOpen && (hasResults || isLoading)"
                            class="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
                            <!-- Loading State -->
                            <div v-if="isLoading" class="p-4 text-center">
                                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto"></div>
                                <p class="text-sm text-gray-500 mt-2 font-poppins">Hľadám...</p>
                            </div>

                            <!-- Results -->
                            <div v-else-if="hasResults" class="max-h-80 overflow-y-auto">
                                <!-- Categories Section -->
                                <div v-if="searchResults.categories.length > 0" class="p-2">
                                    <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                                        Kategórie</h4>
                                    <div v-for="(category, index) in searchResults.categories"
                                        :key="`category-${category.id}`"
                                        class="px-3 py-2 hover:bg-red-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                                        :class="{ 'bg-red-50': selectedIndex === index }"
                                        @click="selectItem({ type: 'category', data: category })">
                                        <div
                                            class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Leaf class="h-4 w-4 text-red-600" />
                                        </div>
                                        <span class="text-sm font-medium text-gray-900 font-poppins"
                                            v-html="highlightMatch(category.name, searchQuery)"></span>
                                    </div>
                                </div>

                                <!-- Sets Section -->
                                <div v-if="searchResults.sets.length > 0" class="p-2 border-t border-gray-100">
                                    <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">Sety</h4>
                                    <div v-for="(set, index) in searchResults.sets"
                                        :key="`set-${set.id}`"
                                        class="px-3 py-2 hover:bg-amber-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                                        :class="{ 'bg-amber-50': selectedIndex === (searchResults.categories.length + index) }"
                                        @click="selectItem({ type: 'set', data: set })">
                                        <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                                            <img v-if="set.main_image" :src="set.main_image" :alt="set.name" class="w-full h-full object-cover" />
                                            <span v-else class="text-amber-600 text-lg">📦</span>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-medium text-gray-900 font-poppins truncate" v-html="highlightMatch(set.name, searchQuery)"></p>
                                            <div class="flex items-center justify-between mt-1">
                                                <span class="text-sm font-semibold text-amber-600">{{ formatPrice(set.price) }}</span>
                                                <span class="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium">Set</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Products Section -->
                                <div v-if="searchResults.products.length > 0" class="p-2 border-t border-gray-100">
                                    <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                                        Produkty</h4>
                                    <div v-for="(product, index) in searchResults.products"
                                        :key="`product-${product.id}`"
                                        class="px-3 py-2 hover:bg-red-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                                        :class="{ 'bg-red-50': selectedIndex === (searchResults.categories.length + searchResults.sets.length + index) }"
                                        @click="selectItem({ type: 'product', data: product })">
                                        <img :src="product.main_image" :alt="product.name"
                                            class="w-10 h-10 object-cover rounded-lg border border-gray-200 flex-shrink-0">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-medium text-gray-900 font-poppins truncate"
                                                v-html="highlightMatch(product.name, searchQuery)"></p>
                                            <div class="flex items-center justify-between mt-1">
                                                <span class="text-sm font-semibold text-red-600">{{
                                                    formatPrice(product.price) }}</span>
                                                <span v-if="product.stock_quantity <= 0"
                                                    class="text-xs text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded-full">Nedostupné</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- View All Results -->
                                <div class="p-3 bg-gray-50 border-t border-gray-100">
                                    <button @click="performSearch"
                                        class="w-full text-left text-sm text-red-600 hover:text-red-700 font-medium font-poppins flex items-center justify-between p-2 hover:bg-white rounded-lg transition-colors">
                                        <span>Všetky výsledky pre "{{ searchQuery }}"</span>
                                        <ArrowRight class="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Section: Actions -->
                <div class="flex items-center space-x-2 lg:space-x-4">

                    <!-- Mobile Search Button -->
                    <button @click="toggleMobileSearch"
                        class="md:hidden p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <Search class="h-5 w-5 text-gray-700" />
                    </button>

                    <!-- Notifications (for authenticated users) -->
                    <!-- <div v-if="isAuthenticated">
                        <NotificationDropdown
                            :button-class="'p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-700'" />
                    </div> -->

                    <!-- Cart -->
                    <div class="relative cart-dropdown">
                        <!-- Mobile: Direct link to cart -->
                        <router-link to="/checkout/cart" class="sm:hidden block relative p-2 hover:bg-gray-100 rounded-full transition-colors group">
                            <ShoppingCart class="h-6 w-6 text-gray-700 group-hover:text-red-600 transition-colors" />
                            <span v-if="cartStore.totalQuantity > 0"
                                class="absolute top-0 right-0 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                                {{ cartStore.totalQuantity > 99 ? '😋' : cartStore.totalQuantity }}
                            </span>
                        </router-link>
                        <!-- Desktop: Dropdown button -->
                        <button @click="toggleCartDropdown"
                            class="hidden sm:block relative p-2 hover:bg-gray-100 rounded-full transition-colors group">
                            <ShoppingCart class="h-6 w-6 text-gray-700 group-hover:text-red-600 transition-colors" />
                            <span v-if="cartStore.totalQuantity > 0"
                                class="absolute top-0 right-0 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                                {{ cartStore.totalQuantity > 99 ? '😋' : cartStore.totalQuantity }}
                            </span>
                        </button>
                    </div>

                    <!-- User Menu -->
                    <div v-if="isAuthenticated" class="relative user-dropdown">
                        <button @click="toggleUserMenu"
                            class="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-full transition-colors group">
                            <div
                                class="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {{ user?.first_name?.charAt(0)?.toUpperCase() || 'U' }}
                            </div>
                            <span
                                class="hidden lg:block text-sm font-medium text-gray-700 group-hover:text-red-600 transition-colors">
                                {{ user?.first_name }}
                            </span>
                            <ChevronDown
                                class="hidden lg:block h-4 w-4 text-gray-500 group-hover:text-red-600 transition-colors" />
                        </button>
                    </div>

                    <!-- Auth Buttons (for non-authenticated users) -->
                    <div v-else class="hidden sm:flex items-center space-x-2">
                        <router-link to="/auth/login"
                            class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors">
                            Prihlásenie
                        </router-link>
                        <router-link to="/auth/register"
                            class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full hover:bg-red-700 transition-colors">
                            Registrácia
                        </router-link>
                    </div>

                    <!-- Mobile Menu Button -->
                    <button @click.stop="toggleMobileMenu"
                        class="lg:hidden p-2 hover:bg-gray-100 rounded-full transition-colors mobile-menu">
                        <Menu v-if="!isMobileMenuOpen" class="h-6 w-6 text-gray-700" />
                        <X v-else class="h-6 w-6 text-gray-700" />
                    </button>
                </div>
            </div>

            <!-- Mobile Search Bar -->
            <div v-if="isMobileSearchOpen"
                class="md:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top duration-300">
                <div class="relative">
                    <input v-model="mobileSearchQuery" type="text" placeholder="Hľadať produkty..."
                        class="w-full pl-12 pr-12 py-3 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all duration-200 font-poppins"
                        @keydown.enter="performMobileSearch" ref="mobileSearchInput">
                    <Search class="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <button @click="performMobileSearch"
                        class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors">
                        <Search class="h-4 w-4" />
                    </button>
                </div>
            </div>

            <!-- Mobile Menu -->
            <div v-if="isMobileMenuOpen"
                class="lg:hidden border-t border-gray-200 bg-white animate-in slide-in-from-top duration-300 mobile-menu">
                <div class="py-4 space-y-2">
                    <!-- Navigation Links -->
                    <div class="space-y-1">
                        <router-link :to="$pagePath('about')"
                            class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg mx-2"
                            :class="{ 'text-red-600 bg-red-50': $route.path === $pagePath('about') }" @click="closeMobileMenu">
                            O nás
                        </router-link>
                        <router-link to="/products"
                            class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg mx-2"
                            :class="{ 'text-red-600 bg-red-50': $route.path === '/products' }" @click="closeMobileMenu">
                            Produkty
                        </router-link>
                        <router-link to="/categories"
                            class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg mx-2"
                            :class="{ 'text-red-600 bg-red-50': $route.path === '/categories' }" @click="closeMobileMenu">
                            Kategórie
                        </router-link>
                        <router-link :to="$pagePath('contact')"
                            class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg mx-2"
                            :class="{ 'text-red-600 bg-red-50': $route.path === $pagePath('contact') }" @click="closeMobileMenu">
                            Kontakt
                        </router-link>
                    </div>

                    <!-- Auth Section -->
                    <div v-if="!isAuthenticated" class="pt-4 border-t border-gray-200 mx-4 space-y-2">
                        <router-link to="/auth/login"
                            class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg"
                            @click="closeMobileMenu">
                            Prihlásenie
                        </router-link>
                        <router-link to="/auth/register"
                            class="block px-4 py-3 bg-red-600 text-white text-center rounded-lg hover:bg-red-700 transition-colors font-medium"
                            @click="closeMobileMenu">
                            Registrácia
                        </router-link>
                    </div>

                    <!-- User Menu (Mobile) -->
                    <div v-else class="pt-4 border-t border-gray-200 mx-4">
                        <div class="flex items-center space-x-3 px-4 py-3 mb-3">
                            <div
                                class="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                                {{ user?.first_name?.charAt(0)?.toUpperCase() || 'U' }}
                            </div>
                            <div>
                                <p class="font-semibold text-gray-900 font-poppins">{{ user?.first_name || 'Používateľ'
                                    }}</p>
                                <p class="text-sm text-gray-600">{{ user?.email }}</p>
                            </div>
                        </div>

                        <div class="space-y-1">
                            <router-link to="/user"
                                class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg"
                                @click="closeMobileMenu">
                                Môj profil
                            </router-link>
                            <router-link to="/user/orders"
                                class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg"
                                @click="closeMobileMenu">
                                Moje objednávky
                            </router-link>

                            <!-- Admin panel for mobile -->
                            <router-link v-if="user?.role === 'admin'" to="/admin"
                                class="block px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium transition-colors rounded-lg"
                                @click="closeMobileMenu">
                                Admin panel
                            </router-link>

                            <button @click="handleLogout"
                                class="block w-full text-left px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium transition-colors rounded-lg">
                                Odhlásiť sa
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Cart Dropdown - Modern Mobile-First Design -->
        <div v-if="isCartDropdownOpen"
            class="fixed sm:absolute left-0 right-0 sm:left-auto sm:right-4 bottom-0 sm:bottom-auto sm:top-full sm:mt-2 w-full sm:w-[420px] max-h-[90vh] sm:max-h-[600px] bg-white/95 sm:bg-white backdrop-blur-xl sm:backdrop-blur-none rounded-t-3xl sm:rounded-2xl shadow-2xl border-t sm:border border-gray-200/50 z-50 animate-in slide-in-from-bottom sm:slide-in-from-top duration-300 cart-dropdown flex flex-col"
            @click.stop>
            
            <!-- Mobile Handle Bar -->
            <div class="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
                <div class="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            <!-- Header -->
            <div class="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="relative">
                            <div class="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/20">
                                <ShoppingCart class="h-5 w-5 text-white" />
                            </div>
                            <div v-if="cartStore.itemCount > 0" 
                                class="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white">
                                {{ cartStore.itemCount > 9 ? '9+' : cartStore.itemCount }}
                            </div>
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-gray-900">Košík</h3>
                            <p class="text-xs text-gray-500">
                                {{ cartStore.itemCount }} {{ cartStore.itemCount === 1 ? 'položka' : cartStore.itemCount < 5 ? 'položky' : 'položiek' }}
                            </p>
                        </div>
                    </div>
                    <button @click="closeCartDropdown" 
                        class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-all active:scale-95">
                        <X class="h-5 w-5 text-gray-400" />
                    </button>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="cartStore.itemCount === 0" class="flex-1 flex flex-col items-center justify-center px-4 py-8 min-h-[300px]">
                <div class="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart class="h-10 w-10 text-gray-400" />
                </div>
                <h4 class="text-base font-semibold text-gray-900 mb-1">Košík je prázdny</h4>
                <p class="text-sm text-gray-500 mb-6">Pridajte produkty do košíka</p>
                <router-link to="/products"
                    class="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 font-medium active:scale-95"
                    @click="closeCartDropdown">
                    <ShoppingCart class="h-4 w-4" />
                    Začať nakupovať
                </router-link>
            </div>

            <!-- Cart Items -->
            <div v-else class="flex flex-col" style="max-height: calc(100vh - 280px);">
                <!-- Items List -->
                <div class="flex-1 overflow-y-auto overscroll-contain">
                    <div class="px-3 sm:px-4 py-2 space-y-2">
                        <div v-for="item in cartStore.cart?.items || []" :key="item.id"
                            class="group relative bg-gray-50/50 hover:bg-gray-50 rounded-xl p-3 transition-all">
                            <div class="flex gap-3">
                                <!-- Image -->
                                <router-link
                                    :to="item.is_set_item ? `/sets/${item.set_metadata?.slug}` : `/products/${item.product?.slug}`"
                                    @click="closeCartDropdown"
                                    class="relative flex-shrink-0">
                                    <div class="w-16 h-16 rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm group-hover:shadow-md transition-shadow">
                                        <img v-if="item.is_set_item && item.set_metadata?.image"
                                            :src="item.set_metadata.image"
                                            :alt="item.set_metadata.name"
                                            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
                                        <img v-else-if="!item.is_set_item && item.product?.main_image"
                                            :src="item.product.main_image"
                                            :alt="item.product.name"
                                            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
                                        <div v-else class="w-full h-full flex items-center justify-center bg-amber-50">
                                            <span class="text-xl">📦</span>
                                        </div>
                                    </div>
                                    <!-- Quantity Badge -->
                                    <div class="absolute -top-1.5 -left-1.5 w-6 h-6 bg-gray-900 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                                        {{ item.quantity }}
                                    </div>
                                </router-link>

                                <!-- Info -->
                                <div class="flex-1 min-w-0">
                                    <router-link
                                        :to="item.is_set_item ? `/sets/${item.set_metadata?.slug}` : `/products/${item.product?.slug}`"
                                        @click="closeCartDropdown">
                                        <h4 class="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-red-600 transition-colors leading-snug mb-1.5">
                                            {{ item.is_set_item ? item.set_metadata?.name : (item.product?.name || 'Product') }}
                                        </h4>
                                    </router-link>

                                    <!-- Set badge -->
                                    <span v-if="item.is_set_item" class="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full mb-1">
                                        Set
                                    </span>

                                    <!-- Pricing Row -->
                                    <div class="flex items-center justify-between gap-2">
                                        <div class="flex items-center gap-1.5 flex-wrap">
                                            <!-- Set pricing -->
                                            <template v-if="item.is_set_item">
                                                <span class="text-sm font-bold text-amber-700">
                                                    {{ formatPrice(item.unit_price) }}
                                                </span>
                                            </template>
                                            <!-- B2B Custom Pricing -->
                                            <template v-else-if="item.product?.has_custom_pricing">
                                                <span class="text-sm font-bold text-blue-600">
                                                    {{ item.product?.formatted_price }}
                                                </span>
                                                <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">
                                                    <Star class="h-2.5 w-2.5" />
                                                    B2B
                                                </span>
                                            </template>
                                            <!-- Regular Pricing -->
                                            <template v-else>
                                                <span class="text-sm font-bold text-gray-900">
                                                    {{ formatPrice(item.final_price) }}
                                                </span>
                                                <span v-if="item.has_bulk_discount"
                                                    class="inline-flex items-center px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded-full">
                                                    -{{ item.discount_percentage }}%
                                                </span>
                                            </template>
                                        </div>

                                        <!-- Total Price -->
                                        <div class="text-right">
                                            <div class="text-base font-black text-gray-900">
                                                {{ item.is_set_item
                                                    ? formatPrice(item.unit_price * item.quantity)
                                                    : item.product?.has_custom_pricing
                                                        ? formatPrice(item.quantity * parseFloat(item.product?.custom_pricing?.custom_price || item.final_price))
                                                        : formatPrice(item.total_price) }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Savings Badge (regular products only) -->
                                    <div v-if="!item.is_set_item && ((item.total_savings > 0 && !item.product?.has_custom_pricing) || 
                                              (item.product?.has_custom_pricing && parseFloat(item.unit_price) > parseFloat(item.product?.custom_pricing?.custom_price || item.final_price)))"
                                        class="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-semibold rounded-md">
                                        <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                        </svg>
                                        Ušetrené {{ item.product?.has_custom_pricing 
                                            ? formatPrice(item.quantity * (parseFloat(item.unit_price) - parseFloat(item.product?.custom_pricing?.custom_price || item.final_price)))
                                            : formatPrice(item.total_savings) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer - Sticky -->
                <div class="border-t border-gray-200 bg-white">
                    <!-- Total Savings Banner -->
                    <div v-if="cartStore.totalSavings > 0" 
                        class="mx-3 sm:mx-4 mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <div class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                    <svg class="h-3.5 w-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <span class="text-xs font-semibold text-green-800">Celková úspora</span>
                            </div>
                            <span class="text-sm font-black text-green-700">{{ formatPrice(cartStore.totalSavings) }}</span>
                        </div>
                    </div>
                    
                    <!-- Total -->
                    <div class="px-4 sm:px-5 py-4">
                        <div class="flex items-center justify-between mb-4">
                            <span class="text-sm font-medium text-gray-600">Medzisúčet</span>
                            <div class="text-right">
                                <div class="text-2xl font-black text-gray-900">
                                    {{ formatPrice(cartStore.total) }}
                                </div>
                                <p class="text-xs text-gray-500">+ doprava pri pokladni</p>
                            </div>
                        </div>
                        
                        <!-- Action Buttons -->
                        <div class="flex gap-2">
                            <router-link to="/checkout"
                                class="flex-[2] px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-center rounded-xl transition-all font-bold text-sm shadow-lg shadow-red-600/20 active:scale-95"
                                @click="closeCartDropdown">
                                Do košíka →
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- User Menu Dropdown -->
        <div v-if="isUserMenuOpen && isAuthenticated"
            class="absolute right-4 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-in slide-in-from-top duration-300 user-dropdown">
            <!-- User Header -->
            <div class="bg-white border-b border-gray-200 p-4">
                <div class="flex items-center space-x-3">
                    <div class="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {{ user?.first_name?.charAt(0)?.toUpperCase() || 'U' }}
                    </div>
                    <div>
                        <p class="font-semibold text-gray-900 font-poppins">{{ user?.first_name || 'Používateľ' }}</p>
                        <p class="text-sm text-gray-600">{{ user?.email }}</p>
                    </div>
                </div>
            </div>

            <!-- Menu Items -->
            <div class="p-2">
                <div class="space-y-1">
                    <router-link to="/user"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <User class="h-4 w-4 text-gray-600 group-hover:text-red-600" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Môj profil</p>
                            <p class="text-xs text-gray-500">Nastavenia účtu</p>
                        </div>
                    </router-link>

                    <router-link to="/user/orders"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package class="h-4 w-4 text-gray-600 group-hover:text-red-600" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Moje objednávky</p>
                            <p class="text-xs text-gray-500">História nákupov</p>
                        </div>
                    </router-link>

                    <router-link v-if="user?.role === 'admin'" to="/admin"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Settings class="h-4 w-4 text-gray-600 group-hover:text-red-600" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Admin panel</p>
                            <p class="text-xs text-gray-500">Správa systému</p>
                        </div>
                    </router-link>
                </div>

                <div class="mt-2 pt-2 border-t border-gray-200">
                    <button @click="handleLogout"
                        class="flex items-center space-x-3 w-full px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors group">
                        <div class="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
                            <LogOut class="h-4 w-4 text-red-600" />
                        </div>
                        <span class="text-sm font-medium">Odhlásiť sa</span>
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useProductsStore } from '@/stores/products'
import { useCategoriesStore } from '@/stores/categories'
import { useSearchDropdown } from '@/composables/useSearchDropdown'
import { useB2BPricing } from '@/composables/useB2BPricing'
import NotificationDropdown from '@/components/notifications/NotificationDropdown.vue'
import {
    ShoppingCart,
    ChevronDown,
    ChevronRight,
    Menu,
    X,
    Search,
    ArrowRight,
    Leaf,
    Star,
    User,
    Package,
    Settings,
    LogOut,
    Sparkles,
    Tag,
    TrendingUp
} from 'lucide-vue-next'

const authStore = useAuthStore()
const cartStore = useCartStore()
const productsStore = useProductsStore()
const categoriesStore = useCategoriesStore()
const router = useRouter()
const route = useRoute()

// B2B pricing composable
const { formatPrice } = useB2BPricing()

// Mega menu state
const isProductsMenuOpen = ref(false)
const isCategoriesMenuOpen = ref(false)
const loadingFeaturedProducts = ref(false)
const loadingCategories = ref(false)
let productsMenuTimeout = null
let categoriesMenuTimeout = null

// Computed properties for data from stores
const featuredProducts = computed(() => (productsStore.products || []).slice(0, 6))
const displayCategories = computed(() => (categoriesStore.categories || []).slice(0, 8))

// Component state
const showTopBar = ref(false)
const isCartDropdownOpen = ref(false)
const isUserMenuOpen = ref(false)
const isMobileMenuOpen = ref(false)
const isMobileSearchOpen = ref(false)
const mobileSearchQuery = ref('')
const mobileSearchInput = ref(null)
const searchDropdownRef = ref(null)

// Search dropdown functionality
const {
    searchQuery,
    isDropdownOpen,
    isLoading,
    searchResults,
    selectedIndex,
    hasResults,
    handleInput: handleSearchInput,
    handleKeydown: handleSearchKeydown,
    selectItem,
    navigateToSearch: performSearch,
    clearSearch,
    highlightMatch,
    handleClickOutside: handleSearchClickOutside
} = useSearchDropdown()

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)

// Mega menu methods
const showProductsMenu = () => {
    clearTimeout(productsMenuTimeout)
    isProductsMenuOpen.value = true
    isCategoriesMenuOpen.value = false
    if (featuredProducts.value.length === 0) {
        loadFeaturedProducts()
    }
}

const hideProductsMenu = () => {
    productsMenuTimeout = setTimeout(() => {
        isProductsMenuOpen.value = false
    }, 200)
}

const showCategoriesMenu = () => {
    clearTimeout(categoriesMenuTimeout)
    isCategoriesMenuOpen.value = true
    isProductsMenuOpen.value = false
    if (displayCategories.value.length === 0) {
        loadCategories()
    }
}

const hideCategoriesMenu = () => {
    categoriesMenuTimeout = setTimeout(() => {
        isCategoriesMenuOpen.value = false
    }, 200)
}

const loadFeaturedProducts = async () => {
    if (featuredProducts.value.length > 0) return
    try {
        loadingFeaturedProducts.value = true
        await productsStore.getProducts({
            is_propagated: true,
            per_page: 6,
            page: 1
        })
    } catch (error) {
        console.error('Failed to load featured products:', error)
    } finally {
        loadingFeaturedProducts.value = false
    }
}

const loadCategories = async () => {
    if (displayCategories.value.length > 0) return
    try {
        loadingCategories.value = true
        await categoriesStore.getCategories({
            with_products_count: true,
            per_page: 8,
            page: 1
        })
    } catch (error) {
        console.error('Failed to load categories:', error)
    } finally {
        loadingCategories.value = false
    }
}

const getCategoryIcon = (categoryName) => {
    const icons = {
        'Paprika': '🌶️',
        'Chilli': '🔥',
        'Korenie': '✨',
        'Omáčky': '🍯',
        'Sušené': '🍂',
        'Čerstvé': '🌿'
    }
    return icons[categoryName] || '🌶️'
}

// Cart dropdown methods
const toggleCartDropdown = () => {
    isCartDropdownOpen.value = !isCartDropdownOpen.value
    isUserMenuOpen.value = false
    isMobileMenuOpen.value = false
}

const closeCartDropdown = () => {
    isCartDropdownOpen.value = false
}

// User menu methods
const toggleUserMenu = () => {
    isUserMenuOpen.value = !isUserMenuOpen.value
    isCartDropdownOpen.value = false
    isMobileMenuOpen.value = false
}

const closeUserMenu = () => {
    isUserMenuOpen.value = false
}

// Mobile menu methods
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    isCartDropdownOpen.value = false
    isUserMenuOpen.value = false
}

const closeMobileMenu = () => {
    isMobileMenuOpen.value = false
}

// Mobile search methods
const toggleMobileSearch = () => {
    isMobileSearchOpen.value = !isMobileSearchOpen.value
    isMobileMenuOpen.value = false
    isCartDropdownOpen.value = false
    isUserMenuOpen.value = false

    // Focus the input after opening
    if (isMobileSearchOpen.value) {
        nextTick(() => {
            if (mobileSearchInput.value) {
                mobileSearchInput.value.focus()
            }
        })
    }
}

const performMobileSearch = () => {
    if (mobileSearchQuery.value.trim()) {
        router.push({
            path: '/products',
            query: { search: mobileSearchQuery.value.trim() }
        })
        isMobileSearchOpen.value = false
        mobileSearchQuery.value = ''
    }
}

// Search focus handler
const handleSearchFocus = () => {
    if (searchQuery.value.length >= 2) {
        handleSearchInput(searchQuery.value)
    }
}

// Fix search input handler to extract value from event
const handleSearchInputEvent = (event) => {
    handleSearchInput(event.target.value)
}

// Logout handler
const handleLogout = async () => {
    await authStore.logout()
    closeUserMenu()
    router.push('/')
}

// Click outside handlers
const handleGlobalClickOutside = (event) => {
    // Close search dropdown when clicking outside
    if (searchDropdownRef.value && !searchDropdownRef.value.contains(event.target)) {
        handleSearchClickOutside(event, searchDropdownRef)
    }

    // Close other dropdowns
    if (!event.target.closest('.cart-dropdown')) {
        isCartDropdownOpen.value = false
    }
    if (!event.target.closest('.user-dropdown')) {
        isUserMenuOpen.value = false
    }
    if (!event.target.closest('.mobile-menu')) {
        isMobileMenuOpen.value = false
    }
}

onMounted(() => {
    // Add click outside listener
    document.addEventListener('click', handleGlobalClickOutside)
})

onUnmounted(() => {
    // Clean up event listeners
    document.removeEventListener('click', handleGlobalClickOutside)
})
</script>

<style scoped>
/* Custom animations for mobile elements */
@keyframes slide-in-from-top {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-in {
    animation-duration: 300ms;
    animation-timing-function: ease-out;
    animation-fill-mode: both;
}

.slide-in-from-top {
    animation-name: slide-in-from-top;
}

/* Backdrop blur support */
@supports (backdrop-filter: blur(12px)) {
    .backdrop-blur-md {
        backdrop-filter: blur(12px);
    }
}

/* Smooth transitions */
.transition-colors {
    transition-property: color, background-color, border-color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}

/* Custom scrollbar for cart items */
.max-h-96::-webkit-scrollbar {
    width: 4px;
}

.max-h-96::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Line clamp utility */
.line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}
</style>